// Session and context gates precede route rendering. Every authenticated destination,
// including bookmarked investigations, uses the shared Brief shell.

import { useEffect, useRef, useState, type ReactElement } from "react";

import { actingOrgName, contextName, orgKindOf } from "./api/memberships";
import type { AvailabilityView } from "./api/posture";
import type { SessionResponse } from "./api/session";
import { slackInstallResultOf } from "./api/slackInstall";
import { contextRefusalWords, Notice, Shell } from "./console/chrome";
import { AccessGrantsPage } from "./console/AccessGrantsPage";
import { AccountPage } from "./console/AccountPage";
import { AccountsPage } from "./console/AccountsPage";
import { AuditPage } from "./console/AuditPage";
import { AwsOrganizationScreen } from "./console/AwsOrganizationPage";
import { IntegrationsPage, ProviderPage } from "./console/ConnectionsPages";
import { PostureScreen } from "./console/PosturePage";
import { ContextChoice } from "./console/ContextChoice";
import { CustomersPage } from "./console/CustomersPage";
import { DeletionPending } from "./console/DeleteAccount";
import { InboxPage } from "./console/InboxPage";
import { ManagedBy } from "./console/ManagedBy";
import { OrganisationsPage } from "./console/OrganisationsPage";
import { Door, ResendVerification } from "./console/Doors";
import { OverviewPage } from "./console/OverviewPage";
import { RunsPage } from "./console/RunsPage";
import { SettingsPage } from "./console/SettingsPage";
import { SourcesPage } from "./console/SourcesPage";
import { SpendPage } from "./console/SpendPage";
import { WaitingInvitations } from "./console/WaitingInvitations";
import { MfaChallenge, SignIn } from "./console/SignIn";
import { contextOf, type ConsoleState } from "./console/consoleState";
import { connectRequestOf } from "./console/integrationsStep";
import {
  currentDestination,
  routeFor,
  settingsRedirectOf,
  type DestinationId,
  type Route,
  type SettingsSection,
} from "./console/routes";
import { INBOX_PATH, inboxPath, inboxViewOf } from "./console/inboxView";
import { isLegacyFindings, postureTabOf, postureTabPath } from "./console/postureTabs";
import { navigate, useLocation } from "./console/location";
import { useConsole, type ConsoleActions } from "./console/useConsole";
import { RunDecisions } from "./console/RunDecisions";
import { RunPage } from "./console/RunPage";

export function App() {
  const location = useLocation();
  // The one-page Settings' paths and anchors (#804): rewritten to the section that
  // holds them now, and rendered as that section from the first frame.
  const moved = settingsRedirectOf(location.pathname, location.search, location.hash);
  const { pathname, search } = moved ?? location;
  useEffect(() => {
    if (moved !== null) navigate(`${moved.pathname}${moved.search}${moved.hash}`);
  });
  const { state, actions } = useConsole(pathname, undefined, search);
  // The screens the Inbox took as filters (#808): their paths, a mailed change link's
  // among them, are rewritten to the Inbox's own, with the filter and the item kept.
  const view = inboxViewOf(pathname, search);
  const legacy = view !== null && pathname.replace(/\/$/, "") !== INBOX_PATH;
  useEffect(() => {
    if (legacy && view !== null) navigate(inboxPath(view));
  });
  // The screen Posture took as a tab (#814): its path is rewritten to the tab's.
  const findings = isLegacyFindings(pathname);
  useEffect(() => {
    if (findings) navigate(postureTabPath("findings"));
  });
  return <Console state={state} actions={actions} pathname={pathname} search={search} />;
}

/** What every destination's screen is handed. */
interface ScreenProps {
  state: ConsoleState;
  actions: ConsoleActions;
  session: SessionResponse;
  /** The location, for a screen a link can open on one item. */
  pathname: string;
  search: string;
}

/** One workspace settings section: every section is handed the same reads and acts. */
function settingsScreen(section: SettingsSection, { state, actions, session }: ScreenProps): ReactElement {
  return (
    <SettingsPage
      section={section}
      session={session}
      roles={state.roles}
      rolesError={state.rolesError}
      policy={state.policy}
      policyError={state.policyError}
      onRevisePolicy={actions.revisePolicy}
      members={state.members}
      membersError={state.membersError}
      onRemoveMember={actions.removeMember}
      readRemoval={actions.readRemoval}
      invites={state.invites}
      invitesError={state.invitesError}
      onInviteMember={actions.inviteMember}
      onRevokeInvite={actions.revokeInvite}
      stepUpFactors={actions.stepUpFactors}
      beginPasskey={actions.beginPasskey}
    />
  );
}

/**
 * One screen per destination, keyed by the table's ids.
 *
 * The `Record` is the guarantee: adding a destination to `DESTINATIONS` without
 * a screen here is a type error, so the rail cannot render a link to nowhere.
 * A unit that adds a screen adds one entry to each — Changes with C4,
 * Integrations with C7 — and touches nothing else in this file.
 */
const SCREENS: Record<DestinationId, (props: ScreenProps) => ReactElement> = {
  overview: ({ session, actions }) => (
    <OverviewPage
      key={JSON.stringify([
        session.user.user_id,
        session.workspace_id,
        session.org_id,
        session.acting_org_id,
        session.impersonated_by,
      ])}
      session={session}
      onSessionRead={actions.reload}
    />
  ),
  runs: ({ state, actions }) => (
    <RunsPage
      runs={state.runs}
      error={state.runsError}
      setup={state.setup}
      integrationsStep={state.integrationsStep}
      onReload={actions.reload}
      onLoadMore={() => void actions.loadMoreRuns?.()}
      hasMore={state.runsHasMore}
      loadingMore={state.runsLoadingMore}
    />
  ),
  sources: ({ state, actions, session }) => (
    <SourcesPage
      session={session}
      orgKind={orgKindOf(session, state.memberships, state.orgs)}
      sources={state.sources}
      setup={state.setup}
      error={state.sourcesError}
      onCreate={actions.createSource}
      onSetActive={actions.setSourceActive}
      onSetAutoInvestigate={actions.setSourceAutoInvestigate}
      onDelete={actions.deleteSource}
      onSendTestAlert={actions.sendTestAlert}
      onReload={actions.reload}
      onReadFilter={actions.readSourceFilter}
      onSaveFilter={actions.saveSourceFilter}
      onSetDryRun={actions.setSourceDryRun}
      automation={
        actions.readAutomation && actions.saveDailyCap
          ? { read: actions.readAutomation, saveCap: actions.saveDailyCap }
          : undefined
      }
    />
  ),
  // The Connections pages that were panels on workspace settings (#805).
  integrations: ({ state, actions, session, search }) => (
    <IntegrationsPage
      session={session}
      orgKind={orgKindOf(session, state.memberships, state.orgs)}
      roles={state.roles}
      rolesError={state.rolesError}
      integrations={state.integrations}
      integrationsError={state.integrationsError}
      connect={connectRequestOf(search)}
      slackInstall={slackInstallResultOf(search)}
      deliveries={state.deliveries}
      onCreate={actions.createIntegration}
      onDisable={actions.disableIntegration}
      onEnable={actions.enableIntegration}
      onDelete={actions.deleteIntegration}
      onUpdate={actions.updateIntegration}
      onExpand={actions.readIntegrationDeliveries}
      onRetry={actions.retryDelivery}
      onTest={actions.testIntegration}
      onAwaitCheck={actions.awaitIntegrationCheck}
      onSendTest={actions.sendTestDelivery}
      onSendTestEvent={actions.sendTestEvent}
      onListJira={actions.jiraLister}
      onListSlack={actions.slackLister}
      onRegisterWebhook={actions.registerWebhook}
      onRotateWebhook={actions.rotateWebhookSecret}
      stepUpFactors={actions.stepUpFactors}
      beginPasskey={actions.beginPasskey}
    />
  ),
  provider: ({ state, actions, session }) => (
    <ProviderPage
      session={session}
      orgKind={orgKindOf(session, state.memberships, state.orgs)}
      roles={state.roles}
      rolesError={state.rolesError}
      provider={state.provider}
      providerRefusal={state.providerRefusal}
      providerError={state.providerError}
      setup={state.setup}
      onSetProvider={actions.setProvider}
      onCheckProvider={actions.checkProvider}
    />
  ),
  // Workspace settings, one section per path (#804); `section` picks its panels.
  team: (props) => settingsScreen("team", props),
  policy: (props) => settingsScreen("policy", props),
  account: ({ state, actions, session }) => (
    <AccountPage
      session={session}
      roles={state.roles}
      passkeys={actions.passkeys}
      authenticator={actions.authenticator}
      changePassword={actions.changePassword}
      sessions={actions.sessions}
      apiKeys={actions.apiKeys}
      deletion={
        actions.requestDeletion && {
          request: actions.requestDeletion,
          wouldDeleteAt: actions.wouldDeleteAt,
          stepUpFactors: actions.stepUpFactors,
          beginPasskey: actions.beginPasskey,
        }
      }
    />
  ),
  inbox: ({ state, actions, session, pathname, search }) => {
    // Whatever is answered in the drawer moves to its workspace's answered list.
    const refreshed = async <T,>(answer: Promise<T>): Promise<T> => {
      const result = await answer;
      void actions.refreshInbox?.();
      return result;
    };
    return (
      <InboxPage
        inbox={state.inbox}
        error={state.inboxError}
        address={session.user.email}
        view={inboxViewOf(pathname, search) ?? undefined}
        session={session}
        contextError={state.contextError}
        linkArrived={state.linkArrived ?? null}
        memberships={state.memberships}
        workspaceName={contextName(session, state.memberships)}
        sources={{
          consent: state.consent,
          consentError: state.consentError,
          changes: state.changes,
          changesError: state.changesError,
          access: state.access,
          accessError: state.accessError,
        }}
        actions={{
          answerConsent: (...args) => refreshed(actions.answerConsent(...args)),
          answerChange: (...args) => refreshed(actions.answerChange(...args)),
          answerGrant: (...args) => refreshed(actions.answerGrant(...args)),
          investigateHeld: actions.investigateHeld && ((id) => refreshed(actions.investigateHeld!(id))),
          dismissHeld: actions.dismissHeld && ((id) => refreshed(actions.dismissHeld!(id))),
          accept: actions.acceptWaiting,
          stepUpFactors: actions.stepUpFactors,
          beginPasskey: actions.beginPasskey,
        }}
        onOlder={(group, cursor) => void actions.loadOlderAnswered?.(group, cursor)}
        go={navigate}
      />
    );
  },
  customers: ({ state, actions, session }) => (
    <CustomersPage
      session={session}
      customers={state.customers}
      error={state.customersError}
      onInvite={actions.inviteCustomer}
      onEnter={actions.chooseContext}
      onRequestAgain={actions.requestAgain}
    />
  ),
  accounts: (props) => <AccountsScreen {...props} />,
  // Keyed on the workspace, so a context change reads the new one's (#914).
  awsOrganization: ({ session }) => <AwsOrganizationScreen key={session.workspace_id ?? ""} session={session} />,
  access: ({ state, actions, session }) => (
    <>
      <AccessGrantsPage
        session={session}
        orgKind={orgKindOf(session, state.memberships, state.orgs)}
        grants={state.access}
        error={state.accessError}
        onAnswer={actions.answerGrant}
        onRevoke={actions.revokeGrant}
        stepUpFactors={actions.stepUpFactors}
        beginPasskey={actions.beginPasskey}
      />
      {/* Its own component beside the page rather than in it (#1195). */}
      <ManagedBy partners={session.partners} />
    </>
  ),
  spend: ({ state }) => <SpendPage spend={state.spend} error={state.spendError} />,
  organisations: ({ state, actions, session }) => (
    <OrganisationsPage
      session={session}
      orgs={state.orgs}
      error={state.orgsError}
      onCreate={actions.createOrg}
      listWorkspaces={actions.listOrgWorkspaces}
      onEnter={actions.chooseContext}
    />
  ),
  audit: () => <AuditPage />,
  // Keyed on the workspace, so a context change reads the new one's (#639, D14).
  posture: ({ session, pathname, search }) => (
    <PostureScreen
      key={session.workspace_id ?? ""}
      tab={postureTabOf(pathname, search)}
      onTab={(tab) => navigate(postureTabPath(tab))}
    />
  ),
};

/**
 * The AWS accounts screen, with whether each account can be scanned (#1274). Read on arrival,
 * when an account is added, removed or changes status, on Refresh, after a scan is started, and
 * once a "next scan allowed at" time has passed. Never on each re-read of the list: while a
 * connection is pending the list is read every `PENDING_POLL_MS`, and on dev every read of the
 * availability is an SSM read of the batch marker. A read that fails leaves the cards without
 * the control.
 */
function AccountsScreen({ state, actions, session }: ScreenProps) {
  const [availability, setAvailability] = useState<AvailabilityView | null>(null);
  const [asked, setAsked] = useState(0);
  const read = useRef(actions.readScanAvailability);
  read.current = actions.readScanAvailability;
  const workspace = session.workspace_id;
  const accounts = state.accounts?.accounts.map((a) => `${a.id}:${a.status}`).join(",") ?? null;
  useEffect(() => {
    // After the list is read, so arrival is one read and not one before it and one after.
    if (read.current === undefined || workspace === null || accounts === null) return;
    let live = true;
    read.current().then(
      (answer) => live && setAvailability(answer ?? null),
      () => live && setAvailability(null),
    );
    return () => {
      live = false;
    };
  }, [workspace, accounts, asked]);
  // The soonest time a refusal names; once it has passed, what it said has changed.
  const due = Math.min(
    ...(availability?.accounts ?? []).flatMap((a) => (a.next_allowed_at ? [Date.parse(a.next_allowed_at)] : [])),
  );
  useEffect(() => {
    if (!Number.isFinite(due)) return;
    const timer = setTimeout(() => setAsked((n) => n + 1), Math.max(0, due - Date.now()) + 1000);
    return () => clearTimeout(timer);
  }, [due]);
  const runScan = actions.runScan;
  const scans =
    runScan === undefined || actions.readScanAvailability === undefined
      ? undefined
      : {
          availability,
          onRun: async (cloudAccountId: string) => {
            try {
              await runScan(cloudAccountId);
            } finally {
              setAsked((n) => n + 1);
            }
          },
        };
  return (
    <AccountsPage
      session={session}
      orgKind={orgKindOf(session, state.memberships, state.orgs)}
      accounts={state.accounts}
      setup={state.setup}
      error={state.accountsError}
      roles={state.roles}
      rolesError={state.rolesError}
      onStart={actions.startConnection}
      onRename={actions.renameAccount}
      onDisconnect={actions.disconnectAccount}
      onRelaunch={actions.relaunch}
      onCheck={actions.checkConnection}
      onCancel={actions.cancelConnection}
      onWatch={actions.watchConnection}
      onReload={() => {
        setAsked((n) => n + 1);
        actions.reload();
      }}
      onSetDryRun={actions.setSourceDryRun}
      scans={scans}
    />
  );
}

/**
 * Which screen this browser gets. The gate first, then the location.
 *
 * Separate from `App` so it is a function of its arguments and nothing else:
 * `App` reads `window.location` and opens the session, and every screen choice
 * below is decided by the state handed in. That is what the tests drive.
 */
export function Console({
  state,
  actions,
  pathname,
  search = "",
}: {
  state: ConsoleState;
  actions: ConsoleActions;
  pathname: string;
  search?: string;
}) {
  const { gate } = state;
  const signOut = () => void actions.signOut();
  if (gate.kind === "loading") {
    return (
      <main className="centre">
        <p className="muted" role="status">
          Reading this session…
        </p>
      </main>
    );
  }
  // An account being deleted is shown one screen in place of every page (#1136), a
  // door and the public page included: the session reaches nothing else.
  if (gate.kind === "deletion-pending") {
    return (
      <DeletionPending
        deletesAt={gate.deletesAt}
        onCancel={actions.cancelDeletion}
        onSignOut={actions.signOut}
      />
    );
  }
  // The doors a mail opens come before the gate (#415): an invitation is held
  // by someone with no session, and a verification link must work either way.
  // The MFA challenge still wins, because a half-finished login is a door of
  // its own that nothing else may open. A door held by a pending deletion (#1171) is
  // not drawn to someone who signed out from the single screen: they land at sign-in.
  const early: Route = routeFor(pathname, search);
  const held = state.doorHeld === true && gate.kind === "signed-out";
  if (early.kind === "door" && gate.kind !== "mfa" && !held) {
    return (
      <Door
        door={early.door}
        token={early.token}
        state={state.door}
        holder={gate.kind === "ready" ? { signedIn: true, session: gate.session } : { signedIn: false }}
        actions={actions}
      />
    );
  }
  if (gate.kind === "signed-out") {
    return <SignIn notice={gate.notice} onSignIn={actions.signIn} />;
  }
  if (gate.kind === "mfa") {
    return (
      <MfaChallenge
        mfaToken={gate.mfaToken}
        methods={gate.methods}
        onComplete={actions.completeMfa}
        beginPasskeyLogin={actions.beginPasskeyLogin}
      />
    );
  }
  if (gate.kind === "no-context") {
    return (
      <ContextChoice
        user={gate.user}
        memberships={state.memberships}
        error={state.membershipsError ?? (state.contextRefused
          ? contextRefusalWords(state.contextRefused)
          : state.contextError ? "We couldn't open your selection. Try again." : null)}
        waiting={state.waiting}
        waitingError={state.waitingError}
        onChoose={actions.chooseContext}
        onAccept={actions.acceptWaiting}
        onResend={actions.resendVerification}
        onRetry={actions.reload}
        onSignOut={signOut}
        platform={
          gate.user.superadmin
            ? {
                orgs: state.orgs,
                error: state.orgsError,
                listWorkspaces: actions.listOrgWorkspaces,
                onEnter: actions.chooseContext,
              }
            : undefined
        }
      />
    );
  }
  const route: Route = early;
  // An unknown path is the list rather than a blank: the control plane serves
  // this page behind every path it does not claim, so a typo is a location the
  // browser reaches, and it must land somewhere a person can act.
  const current = currentDestination(route, gate.session);
  const screen = SCREENS[current ?? "runs"];
  return (
    <Shell
      key={contextOf(gate) ?? "none"}
      session={gate.session}
      current={current}
      contextName={contextName(gate.session, state.memberships, state.orgs)}
      actingOrg={actingOrgName(gate.session, state.memberships)}
      onSignOut={signOut}
      runs={state.runs ?? []}
      memberships={state.memberships ?? []}
      orgs={state.orgs}
      onChooseContext={actions.chooseContext}
      counts={{ inbox: state.inbox?.count ?? 0 }}
      contextRefused={state.contextRefused}
    >
      {state.contextError && !state.contextRefused ? <Notice tone="bad">{state.contextError} Try choosing your workspace again.</Notice> : null}
      {/* The invitations waiting for someone who already holds a workspace (#440):
          a managed provider asked into a second, or a colleague into a customer's.
          A strip rather than a screen — it is news, not the task in hand — and it
          sits above the destination's own content because accepting it changes
          which workspace that content belongs to. */}
      <WaitingInvitations
        invitations={state.waiting ?? []}
        address={gate.session.user.email}
        error={state.waitingError}
        onAccept={actions.acceptWaiting}
        shape="strip"
      />
      {gate.session.user.email_verified ? null : (
        <div className="stack stack--tight">
          <Notice>
            Your email address is not verified yet. Open the link we sent to{" "}
            {gate.session.user.email}, or ask for a new one.
          </Notice>
          <ResendVerification onResend={actions.resendVerification} />
          <div className="divider" />
        </div>
      )}
      {route.kind === "run" ? (
        <RunPage key={`${contextOf(gate)}:${route.runId}`} runId={route.runId} decisions={(projection) => <RunDecisions runId={route.runId} projection={projection} session={gate.session} state={state} actions={actions} />} />
      ) : screen({ state, actions, session: gate.session, pathname, search })}
    </Shell>
  );
}
